import {
  Button,
  Chip,
  Divider,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Tooltip,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getAppointmentContact,
  getAppointmentDate,
  getAppointmentTimeFrom,
  getAppointmentTimeUntil,
} from '../../../api/created/appointment.ts';
import {
  getGetAppointmentDetailsQueryKey,
  getGetAppointmentsQueryKey,
  useDeleteAppointment,
  useGetAppointmentDetails,
  useMarkAppointmentAsNotAttended,
} from '../../../api/generated/appointment-controller/appointment-controller.ts';
import {
  getGetLabOrderByAppointmentIdQueryKey,
  useGetLabOrderByAppointmentId,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import { LabOrderDto } from '../../../api/generated/model';
import { getAppointmentTypeName } from '../../../utils/appointment.ts';
import { formatDateTime, formatDateWithGermanMonth, formatDateYear } from '../../../utils/date.ts';
import { hasLabOrderResults } from '../../../utils/labOrder.ts';
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog.tsx';
import { CreateLabOrderResultDialog } from '../lab-order-results/CreateLabOrderResultDialog.tsx';
import { LabOrderDetailsDialog } from '../lab-order-results/LabOrderDetailsDialog.tsx';
import { CreateAppointmentDialog } from './CreateAppointmentDialog.tsx';

export function AppointmentDetailsDialog({
  open,
  onClose,
  appointmentId,
}: {
  open: boolean;
  onClose: () => void;
  appointmentId: string;
}) {
  const queryClient = useQueryClient();
  const { data: appointment } = useGetAppointmentDetails(appointmentId);
  const { data: labOrder, isPending: isLabOrderPending } = useGetLabOrderByAppointmentId(appointmentId);
  const [isCreateLabOrderResultDialogOpen, setIsCreateLabOrderResultDialogOpen] = useState(false);
  const [isLabOrderDetailsDialogOpen, setIsLabOrderDetailsDialogOpen] = useState(false);
  const [isDeleteAppointmentConfirmationDialogOpen, setIsDeleteAppointmentConfirmationDialogOpen] = useState(false);
  const [isAdjustAppointmentDialogOpen, setIsAdjustAppointmentDialogOpen] = useState(false);

  const hasNotAttended = appointment?.isAttended === false;
  const appointmentContact = getAppointmentContact(appointment);

  const markAppointmentAsNotAttendedMutation = useMarkAppointmentAsNotAttended({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAppointmentDetailsQueryKey(appointmentId) });
      },
      onError: () => {
        toast.error('Beim markieren des Termins als nicht durchgeführt ist ein Fehler aufgetreten');
      },
    },
  });

  const deleteAppointmentMutation = useDeleteAppointment({
    mutation: {
      onSuccess: () => {
        toast.success('Termin wurde erfolgreich gelöscht');
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim löschen des Termins ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    void queryClient.invalidateQueries({ queryKey: getGetAppointmentsQueryKey() });
    onClose();
  };
  return (
    <>
      {appointment && !isLabOrderPending && (
        <>
          <Modal open={open} onClose={handleOnClose}>
            <ModalDialog sx={{ width: 600 }}>
              <ModalClose />
              <Stack direction={'row'} alignItems={'center'} gap={3}>
                <Typography level={'h4'}>
                  {appointment.patient.lastName.toUpperCase()} {appointment.patient.firstName} (
                  {formatDateYear(appointment.patient.birthDate)})
                </Typography>
                {hasNotAttended && (
                  <Chip variant={'solid'} color={'danger'}>
                    Nicht durchgeführt
                  </Chip>
                )}
              </Stack>
              <Divider />
              <Stack gap={2}>
                <Stack direction={'row'}>
                  <Typography flex={1} level={'title-md'}>
                    Datum
                  </Typography>
                  <Typography flex={3}>
                    {formatDateWithGermanMonth(getAppointmentDate(appointment))}
                    {getAppointmentTimeFrom(appointment) && ` ${getAppointmentTimeFrom(appointment)}`}
                    {getAppointmentTimeUntil(appointment) && `-${getAppointmentTimeUntil(appointment)}`}
                  </Typography>
                </Stack>
                <Stack direction={'row'}>
                  <Typography flex={1} level={'title-md'}>
                    Patient
                  </Typography>
                  <Stack flex={3}>
                    <Typography>
                      {appointment.patient.lastName.toUpperCase()} {appointment.patient.firstName}
                    </Typography>
                    <Typography level={'body-sm'}>{appointment.patient.phone}</Typography>
                    <Typography level={'body-sm'}>{appointment.patient.email}</Typography>
                  </Stack>
                </Stack>
                <Stack direction={'row'}>
                  <Typography flex={1} level={'title-md'}>
                    Termin-Typ
                  </Typography>
                  <Stack flex={3}>
                    <Typography>{getAppointmentTypeName(appointment.type)}</Typography>
                  </Stack>
                </Stack>
                <Stack direction={'row'}>
                  <Typography flex={1} level={'title-md'}>
                    Mitarbeiter
                  </Typography>
                  <Typography flex={3}>
                    {appointmentContact?.firstName} {appointmentContact?.lastName}
                  </Typography>
                </Stack>
                {appointment.note && (
                  <Stack direction={'row'}>
                    <Typography flex={1} level={'title-md'}>
                      Bemerkung
                    </Typography>
                    <Typography flex={3}>{appointment.note}</Typography>
                  </Stack>
                )}
                <Stack>
                  <Typography level={'title-md'}>Test Resultat</Typography>

                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <LabOrderResultChip labOrder={labOrder} />
                    {labOrder ? (
                      <Button onClick={() => setIsLabOrderDetailsDialogOpen(true)}>Resultat anzeigen</Button>
                    ) : (
                      <Button disabled={hasNotAttended} onClick={() => setIsCreateLabOrderResultDialogOpen(true)}>
                        Resultat erfassen
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Divider />
                <Stack>
                  <Stack direction={'row'}>
                    <Typography level={'title-sm'} flex={1}>
                      Erstellt
                    </Typography>
                    <Typography level={'body-sm'} flex={5}>
                      {formatDateTime(appointment.created)} - {appointment.createdBy?.firstName}{' '}
                      {appointment.createdBy?.lastName}
                    </Typography>
                  </Stack>
                  {formatDateTime(appointment.created) !== formatDateTime(appointment.updated) && (
                    <Stack direction={'row'}>
                      <Typography level={'title-sm'} flex={1}>
                        Bearbeitet
                      </Typography>
                      <Typography level={'body-sm'} flex={5}>
                        {formatDateTime(appointment.updated)} - {appointment.updatedBy?.firstName}{' '}
                        {appointment.updatedBy?.lastName}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Button
                    disabled={!!labOrder || appointment.isAttended !== null}
                    loading={markAppointmentAsNotAttendedMutation.isPending}
                    onClick={() => !labOrder && markAppointmentAsNotAttendedMutation.mutate({ id: appointmentId })}
                    endDecorator={<Icons.EventBusy />}
                    color={'neutral'}>
                    Nicht durchgeführt
                  </Button>
                  <Stack direction={'row'} gap={1}>
                    <Button
                      onClick={() => setIsAdjustAppointmentDialogOpen(true)}
                      endDecorator={<Icons.Edit />}
                      color={'neutral'}>
                      Bearbeiten
                    </Button>
                    <Button
                      disabled={!!labOrder || appointment.isAttended === true}
                      loading={deleteAppointmentMutation.isPending}
                      onClick={() => !labOrder && setIsDeleteAppointmentConfirmationDialogOpen(true)}
                      endDecorator={<Icons.Delete />}
                      color={'danger'}>
                      Löschen
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </ModalDialog>
          </Modal>
          <CreateLabOrderResultDialog
            open={isCreateLabOrderResultDialogOpen}
            onClose={() => {
              void queryClient.invalidateQueries({ queryKey: getGetAppointmentDetailsQueryKey(appointmentId) });
              void queryClient.invalidateQueries({ queryKey: getGetLabOrderByAppointmentIdQueryKey(appointmentId) });
              setIsCreateLabOrderResultDialogOpen(false);
            }}
            patientId={appointment?.patient.id}
            appointment={appointment}
          />
          {labOrder && (
            <LabOrderDetailsDialog
              open={isLabOrderDetailsDialogOpen}
              onClose={() => {
                void queryClient.invalidateQueries({ queryKey: getGetAppointmentDetailsQueryKey(appointmentId) });
                void queryClient.invalidateQueries({ queryKey: getGetLabOrderByAppointmentIdQueryKey(appointmentId) });
                setIsLabOrderDetailsDialogOpen(false);
              }}
              onEditDialogClose={() => {
                void queryClient.invalidateQueries({ queryKey: getGetAppointmentDetailsQueryKey(appointmentId) });
                void queryClient.invalidateQueries({ queryKey: getGetLabOrderByAppointmentIdQueryKey(appointmentId) });
              }}
              labOrder={labOrder}
            />
          )}
          <DeleteConfirmationDialog
            open={isDeleteAppointmentConfirmationDialogOpen}
            onCancel={() => setIsDeleteAppointmentConfirmationDialogOpen(false)}
            onDelete={() => {
              deleteAppointmentMutation.mutate({ id: appointmentId });
            }}
            modalTitle={'Termin löschen'}>
            <Typography>Ausgewählten Termin wirklich löschen?</Typography>
          </DeleteConfirmationDialog>
          <CreateAppointmentDialog
            open={isAdjustAppointmentDialogOpen}
            onClose={() => {
              void queryClient.invalidateQueries({ queryKey: getGetAppointmentDetailsQueryKey(appointmentId) });
              setIsAdjustAppointmentDialogOpen(false);
            }}
            currentAppointment={appointment}
          />
        </>
      )}
    </>
  );
}

function LabOrderResultChip({ labOrder }: { labOrder: LabOrderDto | undefined }) {
  if (!labOrder) {
    return (
      <Tooltip title={'Für diesen Termin wurde noch kein Test erfasst'}>
        <Chip>Nicht Vorhanden</Chip>
      </Tooltip>
    );
  }

  if (hasLabOrderResults(labOrder)) {
    return <Chip color={'success'}>Vorhanden</Chip>;
  }
  return (
    <Tooltip title={'Für diesen Termin wurde ein Test erfasst, aber noch kein Resultat eingetragen'}>
      <Chip color={'warning'}>Ausstehend</Chip>
    </Tooltip>
  );
}
